"use client";

import { type CSSProperties, useEffect } from "react";
import { FeedEvents, trackEvent } from "@/analytics";
import type { Comedian } from "@/data/comedians";
import type { ComedianWithImageUrl } from "./types";
import Image from "next/image";
import Link from "next/link";
import { PunchupButton } from "@punchup/ui-common/punchup/primitives/buttons/PunchupButton";
import { PunchupHeroCard } from "@punchup/ui-common/punchup/composites/marketing/PunchupHeroCard";
import { PunchUpLogo } from "@/components/Logo";
import { cn } from "@/utils/cn";
import { getCDNImageURL } from "@/data/images";
import styles from "./FeedHero.module.css";

const WALL_COLUMNS = 5;
const TILES_PER_COLUMN = 4;
const WALL_MIN_FACES = 3;

/** Per-column marquee speeds (s per loop). Staggered speeds read as depth. */
const COLUMN_DURATIONS = [42, 54, 46, 58, 50];

/** Portrait crop for wall tiles (2x of the ~130px rendered width). */
const WALL_IMG_PARAMS = "gravity=auto,fit=crop,height=360,width=270";

/**
 * One face per artist, suggested first, then every other artist appearing in
 * the feed payload — so the wall shows the breadth of the roster and only
 * cycles when the whole pool is exhausted.
 */
function collectFaces(
  suggested: ComedianWithImageUrl[],
  pool: Record<string, Comedian>
): string[] {
  const seen = new Set<string>();
  const faces: string[] = [];
  for (const comedian of suggested) {
    if (comedian.imageUrl && !seen.has(comedian.id)) {
      seen.add(comedian.id);
      faces.push(comedian.imageUrl);
    }
  }
  for (const comedian of Object.values(pool)) {
    if (comedian.about_img && !seen.has(comedian.id)) {
      seen.add(comedian.id);
      faces.push(getCDNImageURL(comedian.about_img, WALL_IMG_PARAMS));
    }
  }
  return faces;
}

/**
 * Round-robin the faces across columns so a repeat (only possible when the
 * pool is smaller than the wall) lands in a different column and row — never
 * two identical columns, never the same face twice in one column until the
 * pool is exhausted.
 */
function buildWall(urls: string[]): string[][] {
  const columns: string[][] = Array.from({ length: WALL_COLUMNS }, () => []);
  const total = WALL_COLUMNS * TILES_PER_COLUMN;
  for (let i = 0; i < total; i++) {
    columns[i % WALL_COLUMNS].push(urls[i % urls.length]);
  }
  return columns;
}

/**
 * The logged-out home-feed brand moment — the directory's design language in
 * card form: a full-bleed, dimmed, slowly-scrolling wall of real artist faces
 * behind a stacked display headline. Rendered only for anonymous
 * visitors behind the feed_brand_moment gate (see lib/feed-brand-moment-gate);
 * it owns the page's h1, so FeedHeader hides its "Your Feed" title while this
 * is shown.
 */
export default function FeedHero({
  comedians = [],
  comedianPool = {},
}: {
  comedians?: ComedianWithImageUrl[];
  /** Every comedian in the feed payload — widens the wall beyond the suggested handful. */
  comedianPool?: Record<string, Comedian>;
}) {
  useEffect(() => {
    trackEvent(FeedEvents.VIEWED_FEED_HERO, {});
  }, []);

  const faces = collectFaces(comedians, comedianPool);
  const wall = faces.length >= WALL_MIN_FACES ? buildWall(faces) : null;

  return (
    <div className="px-4 md:px-0 pb-3 md:pb-6">
      <PunchupHeroCard
        decorative={
          wall ? (
            <div className="absolute inset-0">
              <div className="absolute -inset-x-2 -top-4 bottom-auto h-[200%] flex gap-2">
                {wall.map((column, i) => (
                  <div
                    key={i}
                    className={cn(
                      "flex-1",
                      // Three columns carry the mobile wall; all five at md+.
                      i >= 3 ? "hidden md:block" : "block"
                    )}
                  >
                    <div
                      className={styles.marquee}
                      data-direction={i % 2 === 1 ? "down" : "up"}
                      style={
                        {
                          "--marquee-duration": `${COLUMN_DURATIONS[i]}s`,
                        } as CSSProperties
                      }
                    >
                      {/* The stack twice over = a seamless loop. */}
                      {[0, 1].map((copy) => (
                        <div key={copy} className="flex flex-col gap-2">
                          {column.map((url, j) => (
                            <div
                              key={`${copy}-${j}`}
                              className="relative w-full aspect-[3/4] rounded-lg overflow-hidden shrink-0"
                            >
                              <Image
                                src={url}
                                alt=""
                                fill
                                sizes="180px"
                                className="object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {/* Dim scrim — heavier where the copy sits, faces stay legible right. */}
              <div className="absolute inset-0 bg-gradient-to-r from-[rgba(11,9,7,0.94)] via-[rgba(11,9,7,0.82)] to-[rgba(11,9,7,0.45)]" />
            </div>
          ) : undefined
        }
        headline={
          <span className="block select-none font-punchup-display uppercase leading-[1.04] tracking-normal text-[2.75rem] md:text-[4.5rem]">
            <span className="block">Welcome to</span>
            <span className="block">
              {/* Cap height matches the font's 0.859em; the offsets seat it on the baseline and under the W's left edge. */}
              <PunchUpLogo
                role="img"
                aria-label="Punchup"
                className="ml-[0.008em] inline-block h-[0.8875em] w-auto align-[-0.0155em] fill-[var(--punchup-color-primary)]"
              />
            </span>
          </span>
        }
        body="Clips, specials, and live shows straight from artists you love."
        actions={
          <>
            <PunchupButton size="lg" asChild>
              <Link
                href="/signin"
                onClick={() =>
                  trackEvent(FeedEvents.CLICKED_FEED_HERO_CTA, {
                    target: "sign-up",
                  })
                }
              >
                Sign up
              </Link>
            </PunchupButton>
            <PunchupButton variant="secondary-inverse" size="lg" asChild>
              <Link
                href="/directory"
                onClick={() =>
                  trackEvent(FeedEvents.CLICKED_FEED_HERO_CTA, {
                    target: "explore-artists",
                  })
                }
              >
                Explore artists
              </Link>
            </PunchupButton>
          </>
        }
      />
    </div>
  );
}
